/**
 * TanStack Router → Forge `trackView` bridge. Subscribes to `onResolved`
 * (post-navigation) and forwards a normalized page name (not the raw
 * pathname) so dashboards can chart distinct views without payload
 * filtering.
 *
 * Forge groups views by `name`; sending raw paths would explode cardinality
 * (every room code → its own view). The path → name table is an allowlist:
 * unknown paths fall back to `unknown` so leaks of new dynamic segments
 * don't escape to the dashboard.
 */
import type { AnyRouter } from '@tanstack/react-router';
import { trackView } from './client';

const STATIC_NAMES: Record<string, string> = {
  '/': 'home',
  '/online/create': 'online_create',
  '/standalone/setup': 'standalone_setup',
  '/standalone/play': 'standalone_play',
};

const normalize = (path: string): string | null => {
  if (path.startsWith('/dev')) return null;
  const known = STATIC_NAMES[path];
  if (known) return known;
  if (/^\/room\/[^/]+$/.test(path)) return 'online_play';
  return 'unknown';
};

export const subscribeRouterToAnalytics = (router: AnyRouter): (() => void) => {
  let lastName: string | null = null;
  return router.subscribe('onResolved', ({ toLocation }) => {
    const name = normalize(toLocation.pathname);
    if (!name) return;
    if (name === lastName) return;
    const from = lastName ?? undefined;
    lastName = name;
    trackView(name, from ? { userData: { from } } : undefined);
  });
};
